import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { DatasourcesDisplayGridRowDataFragment } from '../../../gen-src/queries/DatasourcesDisplayGridRowDataFragment';
import { DatasourcesDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/DatasourcesDisplayGridRowDataFragmentWithoutPersonalSpace';
import {
  TableUpstreamDatasourcesFilteredByColumnQuery,
  TableUpstreamDatasourcesFilteredByColumnQueryVariables
} from '../../../gen-src/queries/TableUpstreamDatasourcesFilteredByColumnQuery';
import {
  TableUpstreamDatasourcesFilteredByColumnWithoutPersonalSpaceQuery,
  TableUpstreamDatasourcesFilteredByColumnWithoutPersonalSpaceQueryVariables
} from '../../../gen-src/queries/TableUpstreamDatasourcesFilteredByColumnWithoutPersonalSpaceQuery';
import {
  TableUpstreamDatasourcesQuery,
  TableUpstreamDatasourcesQueryVariables,
} from '../../../gen-src/queries/TableUpstreamDatasourcesQuery';
import {
  TableUpstreamDatasourcesWithoutPersonalSpaceQuery,
  TableUpstreamDatasourcesWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/TableUpstreamDatasourcesWithoutPersonalSpaceQuery';
import { PublishedDatasourceSortOrder } from '../../../gen-src/queries/globalTypes';
import { TableUpstreamDatasourcesFilteredByColumnQueryGql } from '../../queries/TableUpstreamDatasourcesFilteredByColumnQueryGql';
import { TableUpstreamDatasourcesFilteredByColumnWithoutPersonalSpaceQueryGql } from '../../queries/TableUpstreamDatasourcesFilteredByColumnWithoutPersonalSpaceQueryGql';
import { TableUpstreamDatasourcesQueryGql } from '../../queries/TableUpstreamDatasourcesQueryGql';
import { TableUpstreamDatasourcesWithoutPersonalSpaceQueryGql } from '../../queries/TableUpstreamDatasourcesWithoutPersonalSpaceQueryGql';
import {
  DatasourcesDisplayGrid,
  DatasourcesDisplayGridRowData,
  getRowDataForDatasource,
  getRowDataForDatasourceWithoutPersonalSpace
} from '../sharedDisplayGrid/DatasourcesDisplayGrid';
import { DatasourcesValidSortFields } from '../sorting/sortProviders/DatasourcesGridSortProvider';

type QueryVariables = TableUpstreamDatasourcesQueryVariables;
type QueryResults = TableUpstreamDatasourcesQuery;
type QueryResultListNode = DatasourcesDisplayGridRowDataFragment;
type GridRowData = DatasourcesDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = TableUpstreamDatasourcesWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = TableUpstreamDatasourcesWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = DatasourcesDisplayGridRowDataFragmentWithoutPersonalSpace;

type QueryVariablesWithFilter = TableUpstreamDatasourcesFilteredByColumnQueryVariables;
type QueryResultsWithFilter = TableUpstreamDatasourcesFilteredByColumnQuery;

type QueryVariablesWithFilterWithoutPersonalSpace = TableUpstreamDatasourcesFilteredByColumnWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = TableUpstreamDatasourcesFilteredByColumnWithoutPersonalSpaceQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  return table.upstreamDatasourcesConnection;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  return table.upstreamDatasourcesConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNode> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  const column = table && table.columns && table.columns[0];
  return column.upstreamDatasourcesConnection;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(
  resultData: QueryResultsWithFilterWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  const column = table && table.columns && table.columns[0];
  return column.upstreamDatasourcesConnection;
}

export type TableUpstreamDatasourcesQueryGridProps =
  GridSortProps<DatasourcesValidSortFields> & {
    readonly tableId: string;
    readonly filteredColumnId: string | null;
    readonly rowCount: number | null;
  };

export function TableUpstreamDatasourcesQueryGrid(props: TableUpstreamDatasourcesQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyUpstreamDatasourcesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredDatasourcesLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredColumnId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={TableUpstreamDatasourcesFilteredByColumnQueryGql}
        partialQueryVariables={{
          tableId: props.tableId,
          columnId: props.filteredColumnId,
          orderBy: props.orderBy as PublishedDatasourceSortOrder
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForDatasource}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={TableUpstreamDatasourcesQueryGql}
        partialQueryVariables={{ tableId: props.tableId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForDatasource}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredColumnId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={TableUpstreamDatasourcesFilteredByColumnWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{
          tableId: props.tableId,
          columnId: props.filteredColumnId,
          orderBy: props.orderBy as PublishedDatasourceSortOrder
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForDatasourceWithoutPersonalSpace}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={TableUpstreamDatasourcesWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ tableId: props.tableId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={getRowDataForDatasourceWithoutPersonalSpace}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    )
  );
}
